/**
 * agreement-markdown.ts — agreement markdown -> the paginated HTML the reader shows.
 *
 * PURE MODULE: no DOM, no Supabase, no `import.meta.env`. `markdown-it` is the only
 * import, which is what lets the unit tests run under the repo's node-environment
 * vitest config. Sanitisation lives next door in `sanitizeAgreementHtml.ts` because
 * DOMPurify needs a window and this file must not.
 *
 * ── WHY THIS MIRRORS THE PDF RENDERER ─────────────────────────────────────────
 * The executed PDF is produced by `generate-agreement/render/markdown.ts` in
 * fleet-backend. A driver who reads one thing on screen and receives another as their
 * signed copy has not really read the document, so the parsing rules here are the same
 * rules, in the same order:
 *
 *   front matter off -> HTML comments out -> `:::` containers split -> markdown-it
 *
 * with the same markdown-it construction (`default` preset for GFM tables, `html:false`,
 * no linkify, no typographer, no soft-break-to-<br>). The containers are the same
 * containers:
 *
 *   :::pagebreak:::            starts a new page in the reader (a page in the PDF)
 *   :::keeptogether ... :::    a block the PDF never splits; a marked group here
 *   :::signature role=X ... :::the ink slot for party X; a marked group here
 *   :::particulars ... :::     the one GFM table inside it is the fixed-label grid
 *   :::box ... :::             the enclosed blocks sit in a bordered panel
 *   :::included ... :::        the one list inside it is marked with green ticks
 *   :::excluded ... :::        the one list inside it is marked with red crosses
 *
 * The last four are PRESENTATION ONLY: they change nothing about which words are on the
 * page or in what order, so every one of them folds to a classed `<div>` — the same shape
 * `:::keeptogether:::` and `:::signature:::` have always folded to — and the look lives
 * entirely in the reader's stylesheet. Emitting no new tag is deliberate: the sanitiser's
 * allowlist does not have to grow to let a new container through, so a container can never
 * be the reason a tag becomes mountable. An unrecognised name stays transparent.
 *
 * ── WHAT SPLITS A PAGE, AND WHAT NEVER DOES ───────────────────────────────────
 * THIS READER PAGINATES ON `:::pagebreak:::` AND ON NOTHING ELSE. There is no measuring
 * pass, so a container is never split by running out of room the way it can be in the PDF:
 * a `:::box:::` or a `:::particulars:::` simply flows inside whichever page it was written
 * on. The one way to divide one is to write a `:::pagebreak:::` inside it, and that is an
 * instruction, not an accident — `PageBuilder` closes every open wrapper at the boundary
 * and reopens it after, so both halves are balanced markup, exactly as `:::keeptogether:::`
 * has always behaved. `.agr-box` additionally carries `break-inside: avoid` in the
 * stylesheet, which costs nothing and keeps the panel whole if a driver prints the page.
 *
 * ── {{signature.*}} IS PRESENTATION, NEVER HASH ───────────────────────────────
 * A document freezes at the FIRST signature, so every later party's signature block is
 * still a literal `{{signature.…}}` inside the frozen text — and must be, because a
 * countersignature cannot be part of the bytes the driver signed. `applySignatureFields`
 * resolves that ONE namespace on the way to the screen, exactly as the PDF resolves it
 * on the way to the page. The hash a signer attests to is always the `content_sha256`
 * the server returned, never anything computed after this pass.
 *
 * Every other `{{field}}` is left EXACTLY as the server sent it. SQL substitutes what it
 * can and leaves the rest literal; the PDF prints those literally too, so the screen and
 * the archived copy disagree in the `signature.` namespace and nowhere else.
 */

import MarkdownIt from 'markdown-it';

// ─── markdown-it configuration (shared with the PDF renderer) ────────────────

/** Identical to `MARKDOWN_IT_OPTIONS` in the edge render module. */
export const MARKDOWN_IT_OPTIONS = {
    html: false,
    linkify: false,
    typographer: false,
    breaks: false,
} as const;

/** The `default` preset is what carries GFM tables; commonmark would drop them. */
export function createMarkdownIt(): MarkdownIt {
    return new MarkdownIt('default', { ...MARKDOWN_IT_OPTIONS });
}

// ─── Source preparation ──────────────────────────────────────────────────────

const HTML_COMMENT = /<!--[\s\S]*?-->/g;

/** Drops every HTML comment. Templates carry transcription notes that must not print. */
export function stripHtmlComments(source: string): string {
    return source.replace(HTML_COMMENT, '');
}

export interface FrontMatterSplit {
    frontMatter: string;
    body: string;
}

/**
 * Splits a leading `---` front-matter block off. Documents rendered from the database
 * arrive without one, but splitting unconditionally makes both shapes safe.
 */
export function splitFrontMatter(source: string): FrontMatterSplit {
    const normalized = source.replace(/^\uFEFF/, '');
    const match = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/.exec(normalized);
    if (!match) return { frontMatter: '', body: normalized };
    return { frontMatter: match[1], body: normalized.slice(match[0].length) };
}

// ─── The signature presentation namespace ────────────────────────────────────

export const SIGNATURE_NAMESPACE = 'signature.';

/** The suffixes `agreement_merge_data` emits per SIGNED party. */
export const SIGNATURE_SUFFIXES = ['name', 'date', 'phone', 'address', 'title'] as const;

/**
 * The fill-in rule printed where a party has not signed yet.
 *
 * The PDF renderer uses bare underscores because its parser never sees them as markdown.
 * Here they DO reach markdown-it, where a run of underscores is an emphasis delimiter or
 * a thematic break, so they are backslash-escaped on the way in and markdown-it prints
 * them back as the same twelve glyphs.
 */
export const MISSING_PLACEHOLDER = '____________';

/**
 * `{{driver.full_name}}` — a dotted path between the braces and NOTHING ELSE.
 *
 * NO INNER WHITESPACE, deliberately, because SQL has none to give. `agreement_render`
 * merges with `replace(body, '{{' || key || '}}', value)` — a literal string swap, not a
 * pattern — so `{{ driver.full_name }}` is text the database never touches, and the PDF
 * renderer, which vendors this same regex, leaves it alone too. A regex here that accepted
 * the spaced form would resolve on screen what the signed copy prints as braces: the driver
 * would read a name where their archived document reads `{{ driver.full_name }}`, which is
 * the exact failure this file exists to prevent.
 *
 * EVERY SEGMENT STARTS WITH A LETTER OR AN UNDERSCORE, the one after a dot included. That is
 * the grammar `agreement_template_publish` refuses a template body against, so no body that
 * reaches this reader can carry a token shaped any other way — and a looser regex here would
 * resolve on screen a form the seal has already ruled unsubstitutable.
 *
 * The spaced form is a template DEFECT rather than a spelling, and it is caught in
 * fleet-admin's template editor, where it can still be fixed before anybody signs.
 */
const MERGE_PATTERN = /\{\{([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\}\}/g;

/** ASCII punctuation markdown gives meaning to, plus `:` (container fences) and `<`/`>`. */
const MARKDOWN_ACTIVE = /[\\`*_{}[\]()#+\-.!|<>~:]/g;

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g;

/** Hard ceiling on one merge value, so a runaway free-text column cannot swallow a page. */
export const MAX_VALUE_LENGTH = 512;

/**
 * Flattens and escapes one presentation value, the same way `merge.ts` does: control
 * characters out, whitespace runs collapsed, then every markdown-active character
 * escaped — so a signer whose name contains `*` cannot bold a clause.
 */
export function sanitizeMergeValue(value: unknown, maxLength = MAX_VALUE_LENGTH): string {
    if (value === null || value === undefined) return '';
    if (typeof value !== 'string') return '';

    let text = value.replace(CONTROL_CHARS, ' ').replace(/\s+/g, ' ').trim();
    if (text.length === 0) return '';
    if (text.length > maxLength) text = `${text.slice(0, maxLength - 1).trimEnd()}…`;
    return text.replace(MARKDOWN_ACTIVE, (ch) => `\\${ch}`);
}

/** Only the `party` is read; an `agreement_render` roster entry satisfies it as it stands. */
export interface SignatureParty {
    party: string;
}

/**
 * Builds the `signature.*` map for one document's signature blocks.
 *
 * `liveValues` is `agreement_render`'s CURRENT merge map — the same map the PDF renderer
 * consumes, not the frozen snapshot, which only ever knew about the first signer. SQL emits
 * a `signature.*` key for a party only once that party's row is SIGNED, and it composes
 * every suffix there: the date from the UTC calendar date, and `.title` from `admins.role`,
 * which is the signer's actual company title. NOTHING IS RE-DERIVED HERE. `role_label` is
 * the template's word for a slot ("Witness to the hirer"), not a job title, and a screen
 * that fills a company title from it prints something the executed PDF does not say.
 *
 * Local derivation survives for exactly one thing: every party on the document is seeded
 * with a blank first, so a party who has NOT signed resolves to the awaiting-signature
 * fill-in rule instead of printing raw braces at a reader. A party absent from both inputs
 * degrades to the same fill-in.
 *
 * `agreement_render` withholds `values` from everyone but an admin, so a driver's reader
 * shows fill-ins where an admin's shows the composed blocks. That is the intended shape: a
 * signature block on a driver's screen either says what the record says or says nothing.
 */
export function signaturePresentationValues(
    parties: readonly SignatureParty[] | null | undefined,
    liveValues: Record<string, unknown> | null | undefined,
): Record<string, string> {
    const values: Record<string, string> = {};

    for (const entry of parties ?? []) {
        const key = String(entry?.party ?? '').toLowerCase();
        if (key.length === 0) continue;
        for (const suffix of SIGNATURE_SUFFIXES) {
            values[`${SIGNATURE_NAMESPACE}${key}.${suffix}`] = '';
        }
    }

    for (const [key, value] of Object.entries(liveValues ?? {})) {
        if (!key.startsWith(SIGNATURE_NAMESPACE)) continue;
        if (typeof value === 'string' && value.length > 0) values[key] = value;
    }

    return values;
}

/**
 * Resolves `{{signature.*}}` and NOTHING else. A field outside the namespace comes back
 * byte-for-byte as it arrived, which is what keeps the reader's text and the hashed text
 * identical everywhere the hash is supposed to cover.
 */
export function applySignatureFields(body: string, values: Record<string, string>): string {
    const escapedPlaceholder = MISSING_PLACEHOLDER.replace(/_/g, '\\_');
    return body.replace(MERGE_PATTERN, (match: string, path: string) => {
        if (!path.startsWith(SIGNATURE_NAMESPACE)) return match;
        const value = sanitizeMergeValue(values[path]);
        return value.length === 0 ? escapedPlaceholder : value;
    });
}

// ─── Container tree ──────────────────────────────────────────────────────────

const CONTAINER_PAGEBREAK = /^:::\s*pagebreak\s*:::\s*$/;
const CONTAINER_OPEN = /^:::\s*([A-Za-z][A-Za-z0-9_-]*)\s*(?:role\s*=\s*([A-Za-z][A-Za-z0-9_]*))?\s*$/;
const CONTAINER_CLOSE = /^:::\s*$/;

type ContainerNode =
    | { type: 'markdown'; text: string }
    | { type: 'pagebreak' }
    | { type: 'container'; name: string; role: string | null; children: ContainerNode[] };

/**
 * Splits `:::` containers out BEFORE markdown-it sees the source, so a fence can never be
 * mistaken for a paragraph and nesting works without a plugin. Unknown container names are
 * transparent; an unclosed container closes at end of input. Same rules as the PDF parser.
 */
function parseContainers(source: string): ContainerNode[] {
    const lines = source.split(/\r?\n/);
    const root: ContainerNode[] = [];
    const stack: ContainerNode[][] = [root];
    let buffer: string[] = [];

    const flush = () => {
        if (buffer.length === 0) return;
        const text = buffer.join('\n');
        if (text.trim().length > 0) stack[stack.length - 1].push({ type: 'markdown', text });
        buffer = [];
    };

    for (const line of lines) {
        if (CONTAINER_PAGEBREAK.test(line)) {
            flush();
            stack[stack.length - 1].push({ type: 'pagebreak' });
            continue;
        }
        const open = CONTAINER_OPEN.exec(line);
        if (open) {
            flush();
            const node: ContainerNode = {
                type: 'container',
                name: open[1].toLowerCase(),
                role: open[2] ? open[2].toUpperCase() : null,
                children: [],
            };
            stack[stack.length - 1].push(node);
            stack.push(node.children);
            continue;
        }
        if (CONTAINER_CLOSE.test(line)) {
            flush();
            if (stack.length > 1) stack.pop();
            continue;
        }
        buffer.push(line);
    }
    flush();
    return root;
}

// ─── Pagination ──────────────────────────────────────────────────────────────

/** True once a chunk of HTML carries at least one character outside a tag. */
function hasVisibleContent(html: string): boolean {
    return html.replace(/<[^>]*>/g, '').trim().length > 0;
}

function attributeEscape(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Accumulates HTML into pages, closing every open container at a page boundary and
 * reopening it on the next page. A `:::keeptogether:::` that somehow spans a break would
 * otherwise emit unbalanced markup into two different `dangerouslySetInnerHTML` roots.
 */
class PageBuilder {
    private readonly pages: string[] = [];
    private buffer: string[] = [];
    private readonly open: string[] = [];

    push(html: string) {
        this.buffer.push(html);
    }

    openGroup(tag: string) {
        this.open.push(tag);
        this.buffer.push(tag);
    }

    closeGroup() {
        this.open.pop();
        this.buffer.push('</div>');
    }

    breakPage() {
        for (let i = 0; i < this.open.length; i++) this.buffer.push('</div>');
        this.flush();
        for (const tag of this.open) this.buffer.push(tag);
    }

    private flush() {
        const html = this.buffer.join('');
        this.buffer = [];
        if (hasVisibleContent(html)) this.pages.push(html);
    }

    finish(): string[] {
        this.flush();
        return this.pages.length > 0 ? this.pages : [''];
    }
}

/**
 * Container name -> the class its wrapper carries. `signature` is not here because it is
 * the one container that also writes an attribute (`data-role`).
 *
 * A name absent from this map is transparent, which is the parity contract with the PDF
 * renderer: a template that reaches this reader carrying a container the renderer has not
 * learned yet must show its CONTENTS unstyled rather than swallow them, so the words on
 * screen and the words on the executed page stay the same words.
 */
const CONTAINER_CLASS: Readonly<Record<string, string>> = {
    keeptogether: 'agr-keep',
    /** Wraps exactly one GFM table; the stylesheet rules it as a fixed-label grid. */
    particulars: 'agr-particulars',
    box: 'agr-box',
    /** Each wraps exactly one list; the stylesheet swaps the bullets for tick/cross marks. */
    included: 'agr-included',
    excluded: 'agr-excluded',
};

function emit(nodes: ContainerNode[], md: MarkdownIt, builder: PageBuilder) {
    for (const node of nodes) {
        if (node.type === 'pagebreak') {
            builder.breakPage();
            continue;
        }
        if (node.type === 'markdown') {
            builder.push(md.render(node.text));
            continue;
        }
        if (node.name === 'signature') {
            const role = node.role ? ` data-role="${attributeEscape(node.role)}"` : '';
            builder.openGroup(`<div class="agr-signature"${role}>`);
            emit(node.children, md, builder);
            builder.closeGroup();
            continue;
        }
        // AN OWN-PROPERTY CHECK, NOT A BARE LOOKUP. The fence name is whatever the template
        // author typed, and the open regex admits any `[A-Za-z][A-Za-z0-9_-]*` — which includes
        // `constructor`, `toString`, `valueOf` and `hasOwnProperty`. A plain
        // `CONTAINER_CLASS[name]` finds those on `Object.prototype` and returns something
        // truthy that is not a class name, so a `:::constructor` fence would emit
        // `<div class="function Object() { [native code] }">` instead of the transparent
        // fallback the parity contract above promises.
        //
        // Spelled `Object.prototype.hasOwnProperty.call` rather than `Object.hasOwn` because
        // this project targets ES2020 and `Object.hasOwn` is ES2022 — it runs everywhere the
        // app ships, but it is not in the `lib` tsc checks against.
        const className = Object.prototype.hasOwnProperty.call(CONTAINER_CLASS, node.name)
            ? CONTAINER_CLASS[node.name]
            : undefined;
        if (className) {
            builder.openGroup(`<div class="${className}">`);
            emit(node.children, md, builder);
            builder.closeGroup();
            continue;
        }
        // Unknown container: transparent, so an unrecognised fence never eats content.
        emit(node.children, md, builder);
    }
}

export interface AgreementPagesOptions {
    /** The party roster, which seeds an unsigned party's fill-in. */
    parties?: readonly SignatureParty[] | null;
    /** `agreement_render`'s live merge map — the only source of a resolved `signature.*`. */
    values?: Record<string, unknown> | null;
    /** Reuse one parser across calls. */
    md?: MarkdownIt;
}

/**
 * Parses agreement markdown into one HTML string per reader page.
 *
 * The HTML is NOT sanitised here — this module is pure and DOMPurify needs a window.
 * Callers render it through `sanitizeAgreementHtml`, never directly.
 */
export function agreementMarkdownToPages(
    source: string | null | undefined,
    options: AgreementPagesOptions = {},
): string[] {
    if (!source || source.trim().length === 0) return [''];

    const md = options.md ?? createMarkdownIt();
    const { body } = splitFrontMatter(source);
    const presented = applySignatureFields(
        stripHtmlComments(body),
        signaturePresentationValues(options.parties, options.values),
    );

    const builder = new PageBuilder();
    emit(parseContainers(presented), md, builder);
    return builder.finish();
}
